import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
} from '@mui/material';

interface Option {
  [key: string]: any;
}

function defaultIsOptionEqualToValue(
  option: Option,
  selected: Option,
  valueKey: string
) {
  if (option === selected) {
    return true;
  }

  if (!option || !selected) {
    return false;
  }

  const optionId =
    option.id ??
    option[valueKey];

  const selectedId =
    selected.id ??
    selected[valueKey];

  if (
    optionId != null &&
    selectedId != null
  ) {
    return (
      String(optionId) ===
      String(selectedId)
    );
  }

  const optionLabel =
    option.name ??
    option.label ??
    option[valueKey] ??
    '';

  const selectedLabel =
    selected.name ??
    selected.label ??
    selected[valueKey] ??
    '';

  return (
    optionLabel !== '' &&
    optionLabel === selectedLabel
  );
}

function mergeSelectedIntoOptions(
  options: Option[],
  value: Option | Option[] | null,
  valueKey: string,
  multiSelect: boolean
) {
  const merged = [...options];

  const seen = new Set(
    options
      .map(
        (option) =>
          option?.[valueKey] ??
          option?.id
      )
      .filter(
        (id) => id != null
      )
      .map((id) => String(id))
  );

  const appendValue = (
    item: Option | null | undefined
  ) => {
    if (!item) {
      return;
    }

    const id =
      item?.[valueKey] ??
      item?.id;

    if (
      id == null ||
      seen.has(String(id))
    ) {
      return;
    }

    merged.push(item);
    seen.add(String(id));
  };

  if (multiSelect) {
    (
      Array.isArray(value)
        ? value
        : []
    ).forEach(appendValue);

    return merged;
  }

  appendValue(
    Array.isArray(value)
      ? value[0]
      : value
  );

  return merged;
}

interface SearchableDropdownProps {
  options: any[];
  labelKey?: string;
  valueKey?: string;
  label?: any;
  value: any | any[] | null;
  onChange: (
    value: any | any[] | null
  ) => void;
  loading?: boolean;
  onSearch?: (
    searchText: string
  ) => void;
  sx?: object;
  multiSelect?: boolean;
  error?: boolean;
  errorMessage?: string;
  fetchMoreOptions?: (
    searchText: string
  ) => void;
  disabled?: boolean;
  getOptionDisabled?: (
    option: any
  ) => boolean;
  getOptionLabel?: (
    option: any
  ) => any;
  isOptionEqualToValue?: (
    option: any,
    value: any
  ) => boolean;
  searchValue?: string;
  size?: 'small' | 'medium';
  className?: string;
  onOpen?: () => void;
  onClose?: () => void;
  slotProps?: Record<
    string,
    unknown
  >;
}

const SearchableDropdown: React.FC<
  SearchableDropdownProps
> = ({
  options = [],
  labelKey = 'label',
  valueKey = 'value',
  label = 'Select an Option',
  value,
  onChange,
  onOpen,
  onClose,
  loading = false,
  onSearch,
  sx = {},
  multiSelect = false,
  error = false,
  errorMessage = 'Invalid',
  fetchMoreOptions,
  disabled = false,
  getOptionDisabled,
  getOptionLabel,
  isOptionEqualToValue,
  searchValue = '',
  size = 'medium',
  className = '',
  slotProps,
}) => {
  const [
    inputValue,
    setInputValue,
  ] = useState<string>('');

  const displayOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        options,
        value,
        valueKey,
        multiSelect
      ),
    [
      options,
      value,
      valueKey,
      multiSelect,
    ]
  );

  const resolveIsOptionEqualToValue =
    useMemo(
      () =>
        isOptionEqualToValue ||
        ((
          option: Option,
          selected: Option
        ) =>
          defaultIsOptionEqualToValue(
            option,
            selected,
            valueKey
          )),
      [
        isOptionEqualToValue,
        valueKey,
      ]
    );

  const resolveOptionLabel = (
    option: any
  ): string => {
    if (option == null) {
      return '';
    }

    if (
      typeof option !== 'object'
    ) {
      return String(option);
    }

    if (getOptionLabel) {
      const customLabel =
        getOptionLabel(option);

      if (
        customLabel != null &&
        String(customLabel).trim() !== ''
      ) {
        return String(customLabel);
      }
    }

    const fallbackLabel =
      option?.[labelKey] ??
      option?.label ??
      option?.[valueKey] ??
      option?.name ??
      '';

    return String(
      fallbackLabel ?? ''
    );
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'input') {
      setInputValue(newValue);

      if (onSearch) {
        onSearch(newValue);
      }

      return;
    }

    if (reason === 'reset') {
      setInputValue(newValue);
      return;
    }

    if (reason === 'clear') {
      setInputValue('');

      if (onSearch) {
        onSearch('');
      }

      return;
    };
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent,
    selectedValue:
      | Option
      | Option[]
      | null
  ) => {
    if (onChange) {
      onChange(selectedValue);
    }

    if (multiSelect) {
      if (
        !selectedValue ||
        selectedValue.length === 0
      ) {
        setInputValue('');

        if (onSearch) {
          onSearch('');
        }
      }
    } else {
      if (onSearch) {
        onSearch('');
      }
    }
  };

  const handleScroll = (
    event: React.SyntheticEvent
  ) => {
    const listboxNode =
      event.currentTarget as HTMLElement;

    if (
      listboxNode.scrollTop +
        listboxNode.clientHeight >=
      listboxNode.scrollHeight
    ) {
      if (fetchMoreOptions) {
        fetchMoreOptions(
          inputValue
        );
      }
    }
  };

  return (
    <Box
      sx={{ width: '100%' }}
      className={
        className || ''
      }
    >
      <Autocomplete
        fullWidth
        size={size}
        multiple={multiSelect}
        onOpen={() => {
          onOpen?.();
        }}
        onClose={() => {
          onClose?.();
        }}
        options={displayOptions}
        isOptionEqualToValue={
          resolveIsOptionEqualToValue
        }
        getOptionLabel={
          resolveOptionLabel
        }
        value={
          value ??
          (multiSelect
            ? []
            : null)
        }
        inputValue={
          searchValue ||
          inputValue
        }
        filterSelectedOptions={
          multiSelect
        }
        onInputChange={
          handleInputChange
        }
        onChange={
          handleOptionSelect
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size="small"
            error={error}
            helperText={
              error
                ? errorMessage
                : null
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}

                  {
                    params
                      .InputProps
                      .endAdornment
                  }
                </>
              ),
            }}
            sx={{
              ...sx,
              width: '100%',
              '& .MuiInputLabel-root': {
                top: '2px',
              },
              '& .MuiInputLabel-shrink': {
                top: '-1px',
              },
            }}
          />
        )}
        ListboxProps={{
          onScroll:
            handleScroll,
        }}
        loading={loading}
        sx={{
          ...sx,
          width: '100%',
        }}
        slotProps={
          slotProps
        }
        disabled={disabled}
        getOptionDisabled={
          getOptionDisabled
        }
      />
    </Box>
  );
};

export default SearchableDropdown;